import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AddEditFormGeneric } from '../../../../ui/generics';
import { OrdersService } from '../../core/services';
import { NotificationService } from '../../../../core/services';
import { OrdersServiceErrorResolverService } from '../../core/services/error-resolvers';
import { ISimpleListItem } from '../../../../core/interfaces/ISimpleListItem';
import { IDeliveryOrder } from '../../core/interfaces/IDeliveryOrder';
import { SuppliersService } from '../../../suppliers/core/services/SuppliersService';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { IDeliveryOrderProduct } from '../../core/interfaces/IDeliveryOrderProduct';
import * as moment from 'moment';
import { OrderTypes } from '../../core/enums';
import { UsersService } from '../../../users/core/services/UsersService';
import { ProductionArticlesService } from '../../../production-planning/core/services/ProductionArticlesService';
import { IProductionArticle } from '../../../production-planning/core/interfaces';
import { environment } from '../../../../../environments/environment';
import { NzUploadChangeParam } from 'ng-zorro-antd/upload';
import { ToolsService } from '../../../../core/services/ToolsService';
import { DeliveryOrderStatuses, UploadDocumentTypes } from '../../../../core/enums';
import { InvoiceTypes } from '../../../../core/enums/InvoiceTypes';
import { IOrderMail } from '../../core/interfaces/IOrderMail';

@Component({
    selector: 'app-add-edit-order-form',
    templateUrl: './add-edit-order-form.component.html',
    styleUrls: ['./add-edit-order-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddEditOrderFormComponent extends AddEditFormGeneric<IDeliveryOrder> implements OnInit {

  @Input() orderType = OrderTypes.MIXED;
  @Output() orderSynced = new EventEmitter<IDeliveryOrder>();

  isUsersLoading = false;
  isInvoiceApproversLoading = false;
  isHandlersLoading = false;
  isSuppliersLoading: boolean;
  usersList: ISimpleListItem[];
  isInvoiceApproversList: ISimpleListItem[];
  handlersList: ISimpleListItem[];
  isArticlesLoading: boolean;
  suppliersList: ISimpleListItem[];
  articlesList: ISimpleListItem[];
  productFormList: FormArray;
  editRow: number;
  searchSupplierChange$ = new Subject();
  searchUsersChange$ = new Subject();
  searchHandlerChange$ = new Subject();
  searchInvoiceApproverChange$ = new Subject();
  searchArticleChange$ = new Subject();
  public additionalProducts: ISimpleListItem[] = [];
  public isAdditionalProductsModalVisible = false;
  private skipSupplierChange = false;
  public isFileUploading = false;
  public readonly environment = environment;
  public readonly orderTypes = OrderTypes;
  public orderMails: IOrderMail[] = [];
  public orderMailsLoading = false;
  public orderMailsError = false;
  public openMailIds = new Set<number>();
  public readonly uploadDocumentTypes = UploadDocumentTypes;
  public readonly invoiceTypes = InvoiceTypes;
  public readonly deliveryOrderStatuses = DeliveryOrderStatuses;
  public rowBookState: Record<number, 'idle' | 'booking' | 'error'> = {};
  public rowIdempotency: Record<number, string> = {};
  public bookAllVisible = false;
  public bookAllBusy = false;
  public bookAllIdempotency: string = null;
  public bookAllDeliveryDate: string = null;
  public bookAllCharge = '';
  public bookAllMhd: string = null;
  public correctVisible = false;
  public correctBusy = false;
  public correctRow: number = null;
  public correctReason = '';
  public correctQuantity: number = null;
  public closeConfirmVisible = false;
  public concurrencyVisible = false;
  public readonly documentKinds = ['Rechnung', 'Lieferschein', 'Sonstiges'];
  public pendingClose: () => void;

  constructor(
    public readonly toolsService: ToolsService,
    protected _fb: FormBuilder,
    protected _service: OrdersService,
    protected _supplierService: SuppliersService,
    protected _notifyService: NotificationService,
    protected _errorResolver: OrdersServiceErrorResolverService,
    private _cdr: ChangeDetectorRef,
    private _renderer: Renderer2,
    private _usersService: UsersService,
    private _productionArticles: ProductionArticlesService,
  ) {
    super(_fb, _service, _notifyService, _errorResolver);
  }

  loadForm(): void {
    if (!this.form) {
      return;
    }

    if (this.editObject?.id) {
      this.loading = true;
      this.isEdit = true;
      this.fetchAndPatchOrder(() => this.loading = false);
      return;
    }

    if (this.editObject) {
      this.isEdit = false;
      this.skipSupplierChange = true;
      this.beforeLoadForm();
      this.form.patchValue(this.editObject);
      this._cdr.detectChanges();
      setTimeout(() => this.skipSupplierChange = false);
    }
  }

  submitForm(): void {
    this.isSubmitted = true;
    for (const key in this.form.controls) {
      if (this.form.controls.hasOwnProperty(key)) {
        this.form.controls[key].markAsDirty();
        this.form.controls[key].updateValueAndValidity();
      }
    }

    if (!this.form.valid) {
      this.formFinished.emit(false);
      return;
    }

    if (this.editObject?.id) {
      this.saveEdit();
      return;
    }

    this.saveNew();
  }

  @HostListener('window:click', ['$event.target'])
  onClickAnywhere(target: HTMLElement) {
    const fId = this._renderer.selectRootElement('.cdk-global-overlay-wrapper > div', true).getAttribute('id');
    if (
      target.closest('.editable-row') === null &&
      target.closest('#add-product-row') === null &&
      target.closest('.cdk-overlay-pane')?.getAttribute('id') === fId
    ) {
      this.editRow = null;
      this._cdr.detectChanges();
    }
  }

  createProductItem(isNew: boolean): FormGroup {
    const obj = {
      articleId: '',
      productionArticleId: null,
      id: -1,
      articleVariantId: '',
      deliveryDate: null,
      mhd: null,
      isBagable: null,
      number: '',
      name: '',
      charge: '',
      quantity: [null, Validators.required],
      checked: false,
      status: 'Open',
      comment: '',
      isNew,
      isBio: null,
      deliveredQuantity: null,
      product: [null, Validators.required],
    };

    return  this._fb.group(obj);
  }

  ngOnInit() {
    const obj = {
      supplier: ['', Validators.required],
      orderer: ['', Validators.required],
      status: [null],
      handler: [null],
      invoiceApprover: [null],
      invoiceType: [null],
      invoiceDate: [null],
      estimatedDelivery: [null],
      orderDate: ['', Validators.required],
      orderDocumentKeys: [[]],
      documents: [[]],
      comment: [''],
      orderType: [this.orderType, Validators.required],
      orderProducts: this._fb.array([]),
      isPaid: [false],
      isInvoiceUploaded: [false],
    };

    if (this.orderType === OrderTypes.MIXED) {
      delete obj.orderer;
    }

    this.form = this._fb.group(obj);

    this.productFormList = this.form.get('orderProducts') as FormArray;

    super.ngOnInit();
    this.loadForm();

    const getUsers = (name: string) =>
      this._usersService.getSimpleList(name);

    const getSuppliers = (name: string) =>
      this._supplierService.getSimpleList(name);

    const getArticles = (name: string) => this.getArticlesForPicker(name);

    const usersOptionList$: Observable<ISimpleListItem[]> = this.searchUsersChange$
      .asObservable()
      .pipe(debounceTime(500))
      .pipe(switchMap(getUsers));
    usersOptionList$.subscribe(data => {
      this.usersList = this.ensureUserInList(data, this.form?.get('orderer')?.value || this.editObject?.orderer);
      this.isUsersLoading = false;
      this._cdr.detectChanges();
    });

    const handlersOptionList$: Observable<ISimpleListItem[]> = this.searchHandlerChange$
      .asObservable()
      .pipe(debounceTime(500))
      .pipe(switchMap(getUsers));
    handlersOptionList$.subscribe(data => {
      this.handlersList = this.ensureUserInList(data, this.form?.get('handler')?.value || this.editObject?.handler);
      this.isHandlersLoading = false;
      this._cdr.detectChanges();
    });

    const invoiceApproverOptionList$: Observable<ISimpleListItem[]> = this.searchInvoiceApproverChange$
      .asObservable()
      .pipe(debounceTime(500))
      .pipe(switchMap(getUsers));
    invoiceApproverOptionList$.subscribe(data => {
      this.isInvoiceApproversList = this.ensureUserInList(
        data,
        this.form?.get('invoiceApprover')?.value || this.editObject?.invoiceApprover
      );
      this.isInvoiceApproversLoading = false;
      this._cdr.detectChanges();
    });

    const suppliersOptionList$: Observable<ISimpleListItem[]> = this.searchSupplierChange$
      .asObservable()
      .pipe(debounceTime(500))
      .pipe(switchMap(getSuppliers));
    suppliersOptionList$.subscribe(data => {
      this.suppliersList = data;
      this.isSuppliersLoading = false;
      this._cdr.detectChanges();
    });

    const articlesOptionList$: Observable<ISimpleListItem[]> = this.searchArticleChange$
      .asObservable()
      .pipe(debounceTime(500))
      .pipe(switchMap(getArticles));
    articlesOptionList$.subscribe(data => {
      this.articlesList = data;
      this.isArticlesLoading = false;
      this._cdr.detectChanges();
    });

    this.searchUsersChange$.next('');
    this.searchHandlerChange$.next('');
    this.searchInvoiceApproverChange$.next('');
  }

  onSearchUsers(value: string) {
    this.isUsersLoading = true;
    this.searchUsersChange$.next(value);
  }

  onSearchInvoiceApprovers(value: string) {
    this.isInvoiceApproversLoading = true;
    this.searchInvoiceApproverChange$.next(value);
  }

  onSearchHandlers(value: string) {
    this.isHandlersLoading = true;
    this.searchHandlerChange$.next(value);
  }

  onSearchSupplier(value: string) {
    this.isSuppliersLoading = true;
    this.searchSupplierChange$.next(value);
  }

  onArticleSearch(value: string) {
    this.isArticlesLoading = true;
    this.searchArticleChange$.next(value);
  }

  startEdit(id: number): void {
    this.editRow = id;
  }

  deleteRow(id: number): void {
    this.productFormList.removeAt(id);
  }

  addProductRow(isNew = true) {
    this.productFormList.push(this.createProductItem(isNew));
    if (isNew) {
      this.editRow = this.productFormList.length - 1;
      this.onArticleSearch('');
    }
  }

  productChange(id: number | string) {
    if (id == null || this.editRow == null) {
      return;
    }

    const article = this.articlesList?.find(value => value.id === id.toString());
    if (!article) {
      return;
    }

    if (this.orderType === OrderTypes.Bagable) {
      this.productFormList.at(this.editRow).patchValue({
        product: article.id,
        name: article.value,
        number: null,
        isBagable: true,
        productionArticleId: Number(article.additionalParameters?.ProductionArticleId || article.id),
        articleId: null,
        articleVariantId: null,
        isBio: article.additionalParameters?.IsBio === 'true' || article.additionalParameters?.IsBio === 'True',
      });
      return;
    }

    this.productFormList.at(this.editRow).patchValue({
      product: article.id,
      name: article.value,
      number: article.additionalParameters?.Number,
      isBagable: article.additionalParameters?.isBagable === 'True',
      articleId: (article.additionalParameters?.ArticleId ? article.additionalParameters.ArticleId : null),
      articleVariantId: (article.additionalParameters?.ArticleVariantId ? article.additionalParameters.ArticleVariantId : null),
      productionArticleId: null,
      isBio: article.additionalParameters?.IsBio !== null,
    });
  }

  copyRow(rowId: number) {
    const newRow = this.createProductItem(true);

    newRow.patchValue({
      ...this.productFormList.at(rowId).getRawValue(),
      id: -1,
      status: 'Open'
    });

    this.productFormList.insert(rowId + 1, newRow);
  }

  bookRow(rowId: number): void {
    this.isSubmitted = true;
    let fg = this.productFormList.at(rowId) as FormGroup;
    fg = this.setValidatorsForBook(fg);

    for (const key in fg.controls) {
      if (fg.controls.hasOwnProperty(key)) {
        fg.controls[key].markAsDirty();
        fg.controls[key].updateValueAndValidity();
      }
    }

    if (!fg.valid) {
      this._cdr.detectChanges();
      this.clearValidators(fg);
      return;
    }

    const orderProduct = fg.getRawValue() as IDeliveryOrderProduct;
    const productId = orderProduct.id;
    const bookingDelivered = orderProduct.deliveredQuantity;
    const serverDelivered = this.editObject?.orderProducts
      ?.find(p => p.id === productId)?.deliveredQuantity ?? null;
    const restoreDelivered = () => fg.patchValue(
      { deliveredQuantity: bookingDelivered },
      { emitEvent: false }
    );

    // PUT persists sibling rows. Booked row keeps last server delivered so BookLine
    // delta = form target - already stocked, not 0 after the PUT.
    fg.patchValue({ deliveredQuantity: serverDelivered }, { emitEvent: false });

    this.saveEdit(() => {
      restoreDelivered();
      if (!this.rowIdempotency[productId]) {
        this.rowIdempotency[productId] = crypto.randomUUID();
      }
      this.rowBookState[productId] = 'booking';
      this._cdr.detectChanges();

      this._service.bookProductOrder({
        ...orderProduct,
        deliveredQuantity: bookingDelivered,
      }, {
        version: this.editObject?.['version'],
        idempotencyKey: this.rowIdempotency[productId],
      }).subscribe({
        next: () => {
          this.rowBookState[productId] = 'idle';
          delete this.rowIdempotency[productId];
          this._notifyService.pushSuccess('Erfolgreich', `Produkt korrekt gebucht`);
          this.fetchAndPatchOrder(() => this._cdr.detectChanges());
        },
        error: (err) => {
          this.rowBookState[productId] = 'error';
          if (err?.InternalCode === 'OrderConcurrencyConflict') {
            this.concurrencyVisible = true;
          }
          this.parseError(err);
          this._cdr.detectChanges();
        },
      });
      this.clearValidators(fg);
    }, restoreDelivered);
  }

  cancelRow(rowId: number): void {
    const fg = this.productFormList.at(rowId) as FormGroup;

    this._service.cancelProductOrder(fg.value.id).subscribe(() => {
        this._notifyService.pushSuccess('Erfolgreich', `Produkt korrekt storniert`);
        fg.patchValue({
          status: 'Canceled'
        });
        this._cdr.detectChanges();
      },
      this.parseError);
  }

  beforeLoadForm() {
    this.productFormList.clear();

    if (this.editObject.orderProducts) {
      this.articlesList = this.editObject.orderProducts.map(product => ({
        id: String(product.articleVariantId || product.articleId),
        value: product.name,
      }));
      this.editObject.orderProducts.forEach(() => this.addProductRow(false));
    }

    if (this.editObject.supplier) {
      const supplierName = this.editObject.supplier.name
        || (this.editObject.supplier as unknown as ISimpleListItem).value;
      this.suppliersList = [{
        id: String(this.editObject.supplier.id),
        value: supplierName,
      }];
      this.searchSupplierChange$.next(supplierName);
    }

    if (this.editObject.orderer) {
      this.usersList = this.ensureUserInList(this.usersList, this.editObject.orderer);
    }

    if (this.editObject.handler) {
      this.handlersList = this.ensureUserInList(this.handlersList, this.editObject.handler);
    }

    if (this.editObject.invoiceApprover) {
      this.isInvoiceApproversList = this.ensureUserInList(this.isInvoiceApproversList, this.editObject.invoiceApprover);
    }
  }

  setValidatorsForBook(fg: FormGroup) {
    fg.get('checked')?.clearValidators();
    fg.get('deliveryDate').setValidators(Validators.required);

    if (this.orderType !== OrderTypes.Bagable) {
      fg.get('deliveredQuantity')?.setValidators([Validators.required, Validators.min(1)]);
    }

    if (this.orderType !== OrderTypes.NonPacking) {
      fg.get('charge').setValidators(Validators.required);
      fg.get('mhd').setValidators(Validators.required);
    }

    fg.get('quantity').setValidators([Validators.required, Validators.min(1)]);

    return fg;
  }

  clearValidators(fg: FormGroup) {
    fg.clearValidators();
    return fg;
  }

  isAllProductsOpen() {
    return this.productFormList.value.find(value => (value.status !== 'Open'));
  }

  saveEdit(onSuccessCb?: () => void, onErrorCb?: () => void): void {
    this.updateDatsFormat();

    if (this.form.value.status === this.editObject.status) {
      this.form.value.status = null;
    }

    const object: IDeliveryOrder = {
      ...this.editObject,
      ...this.form.value,
    };

    object.orderProducts = this.mapOrderProductsForSave(object.orderProducts);

    this._formService.update(object, this.editObject['id']).subscribe(() => {
      if (onSuccessCb) {
        onSuccessCb();
      } else {
        this._notifyService.pushSuccess('Erfolgreich', `Element wurde korrekt aktualisiert.`);
        this.formFinished.emit(true);
      }
    }, err => {
      onErrorCb?.();
      this.parseError(err);
      this.formFinished.emit(false);
    });
  }

  saveNew(): void {
    this.updateDatsFormat();

    const object: IDeliveryOrder = {
      ...this.form.value,
    };

    object.orderProducts = this.mapOrderProductsForSave(object.orderProducts);

    this._formService.create(object).subscribe(() => {
      this._notifyService.pushSuccess('Erfolgreich', `Element wurde korrekt erstellt`);
      this.formFinished.emit(true);
    }, err => {
      this.parseError(err);
      this.formFinished.emit(false);
    });
  }

  public handleChange(info: NzUploadChangeParam): void {
    this.isFileUploading = true;

    switch (info.type) {
      case 'start': {
        this.isFileUploading = true;
        this._notifyService.pushInfo('Infos', 'Dokument wird hochgeladen, das kann einige Zeit dauern');

        break;
      }

      case 'success': {
        this._notifyService.pushSuccess('Erfolg', 'Dokument wurde hochgeladen');
        this.isFileUploading = false;
        const key = info.file.response['key'] as string;
        this.form.get('orderDocumentKeys')
          .patchValue([...this.form.get('orderDocumentKeys').value, key]);
        const docs = [...(this.form.get('documents').value || [])];
        docs.push({
          s3Key: key,
          fileName: info.file.name,
          kind: 'Sonstiges',
          sizeBytes: info.file.size || 0,
          uploadedAt: new Date().toISOString(),
        });
        this.form.get('documents').patchValue(docs);
        this._cdr.detectChanges();
        break;
      }
    }
  }

  public addSuggestedArticleToOrder(val: ISimpleListItem): void {
    this.articlesList = [val];
    this.addProductRow();
    this.productChange(val.id);
    this.editRow = null;
    this._cdr.detectChanges();
  }

  public suggestionLabels(item: ISimpleListItem): string[] {
    const ordered = item.additionalParameters?.['PreviouslyOrdered'] === 'True';
    const assigned = item.additionalParameters?.['Assigned'] === 'True';
    const labels: string[] = [];
    if (ordered) {
      labels.push('Bisher bestellt');
    }
    if (assigned) {
      labels.push('Zugeordnet');
    }
    return labels;
  }

  public articleLink(item: ISimpleListItem): string | null {
    const articleId = item.additionalParameters?.['ArticleId'];
    return articleId ? `/articles/${articleId}` : null;
  }

  public onSupplierChange(): void {
    if (this.skipSupplierChange || this.isEdit) {
      return;
    }

    if (this.orderType === OrderTypes.Bagable) {
      return;
    }

    const supplierId = this.form.get('supplier')?.value?.id;
    if (!supplierId) {
      return;
    }

    const fremdmarke = this.orderType === OrderTypes.NonPacking;
    this.getArticlesForPicker('', supplierId, true, fremdmarke)
      .subscribe(res => {
        if (res.length > 0) {
          this.additionalProducts = res;
          this.isAdditionalProductsModalVisible = true;
          this._cdr.detectChanges();
        }
      });
  }

  private getCountedQuantities(arr: IDeliveryOrderProduct[]): IDeliveryOrderProduct[] {
    return arr.map(x => {
      return {
        ...x,
        quantity: x.isBagable ? x.quantity * 1000 : x.quantity
      };
    });
  }

  private mapOrderProductsForSave(arr: IDeliveryOrderProduct[]): IDeliveryOrderProduct[] {
    const counted = this.getCountedQuantities(arr);
    if (this.orderType !== OrderTypes.Bagable) {
      return counted;
    }

    return counted.map(x => ({
      ...x,
      productionArticleId: x.productionArticleId,
      articleId: null,
      articleVariantId: null,
    }));
  }

  private fetchAndPatchOrder(onComplete: () => void): void {
    const load = () => this._formService.getById(this.editObject['id'].toString()).subscribe(value => {
      value.orderProducts?.forEach(x => {
        if (x.pricePerKg == null) {
          x.pricePerKg = 0;
        }
      });
      this.applyBagableArticleNames(value);
      this.applyOrder(value);
      this.loadOrderMails();
      onComplete();
    });

    if (this.orderType === OrderTypes.Bagable) {
      this._productionArticles.warmCache().subscribe({
        next: () => load(),
        error: () => load()
      });
      return;
    }

    load();
  }

  private getArticlesForPicker(
    search: string,
    supplierId?: number,
    requireSupplier = false,
    includeAssigned = false
  ): Observable<ISimpleListItem[]> {
    if (this.orderType === OrderTypes.Bagable) {
      return this._productionArticles.searchForSelect(search).pipe(
        map(rows => rows
          .filter(article => !article.isOwnProduction)
          .slice(0, 50)
          .map(article => this.toProductionArticleListItem(article)))
      );
    }

    return this._service.getArticlesSimpleList(
      search,
      this.orderType === OrderTypes.NonPacking,
      supplierId,
      false,
      requireSupplier,
      includeAssigned,
      includeAssigned ? 100 : undefined
    );
  }

  private toProductionArticleListItem(article: IProductionArticle): ISimpleListItem {
    return {
      id: String(article.id),
      value: article.name,
      additionalParameters: {
        ProductionArticleId: String(article.id),
        Number: '',
        isBagable: 'True',
        IsBio: article.isBio ? 'True' : 'False',
      }
    };
  }

  private applyBagableArticleNames(order: IDeliveryOrder): void {
    if (this.orderType !== OrderTypes.Bagable || !order.orderProducts) {
      return;
    }

    order.orderProducts.forEach(product => {
      const productionArticle = this.resolveProductionArticle(product);
      if (!productionArticle) {
        return;
      }

      product.productionArticleId = productionArticle.id;
      product.name = productionArticle.name;
      product.number = null;
      product.isBagable = true;
      product.isBio = productionArticle.isBio ?? product.isBio;
    });
  }

  private resolveProductionArticle(product: IDeliveryOrderProduct): IProductionArticle | null {
    if (product.productionArticleId) {
      return this._productionArticles.findById(Number(product.productionArticleId));
    }

    if (product.articleId) {
      return this._productionArticles.listCached()
        .find(article => article.shopwareArticleId === Number(product.articleId)) ?? null;
    }

    return null;
  }

  private loadOrderMails(): void {
    if (this.orderType !== OrderTypes.NonPacking || !this.editObject?.id) {
      this.orderMails = [];
      this.orderMailsLoading = false;
      this.orderMailsError = false;
      return;
    }
    this.orderMailsLoading = true;
    this.orderMailsError = false;
    this._service.getOrderMails(this.editObject.id).subscribe({
      next: mails => {
        this.orderMails = mails || [];
        this.orderMailsLoading = false;
        this._cdr.detectChanges();
      },
      error: () => {
        this.orderMails = [];
        this.orderMailsLoading = false;
        this.orderMailsError = true;
        this._cdr.detectChanges();
      }
    });
  }

  public toggleMail(id: number): void {
    if (this.openMailIds.has(id)) {
      this.openMailIds.delete(id);
    } else {
      this.openMailIds.add(id);
    }
    this._cdr.markForCheck();
  }

  public mailStatusLabel(status: string): string {
    switch (status) {
      case 'Sent':
        return 'Gesendet';
      case 'Failed':
        return 'Fehlgeschlagen';
      case 'Suppressed':
        return 'Nicht gesendet';
      default:
        return '–';
    }
  }

  public mailStatusKind(status: string): string {
    return status === 'Sent' ? 'done' : 'error';
  }

  public joinEmails(emails: string[]): string {
    return emails?.length ? emails.join(', ') : '–';
  }

  private updateDatsFormat(): void {
    this.form.value['orderDate'] = moment(this.form.value['orderDate']).format('YYYY-MM-DD');

    if (this.form.value['invoiceDate']) {
      this.form.value['invoiceDate'] = moment(this.form.value['invoiceDate']).format('YYYY-MM-DD');
    }

    if (this.form.value['estimatedDelivery']) {
      this.form.value['estimatedDelivery'] = moment(this.form.value['estimatedDelivery']).format('YYYY-MM-DD');
    }

    this.form.value['orderProducts'].forEach(x => {
      x.deliveryDate = x.deliveryDate ? moment(x.deliveryDate).format('YYYY-MM-DD') : null;
      x.mhd = x.mhd ? moment(x.mhd).format('YYYY-MM-DD') : null;
    });
  }

  public removeDoc(doc: string): void {
    this.form.get('orderDocumentKeys')
      .patchValue(this.form.get('orderDocumentKeys').value.filter(x => x !== doc));
    const docs = (this.form.get('documents').value || []).filter(d => d.s3Key !== doc && d !== doc);
    this.form.get('documents').patchValue(docs);
  }

  public documentsList(): { s3Key: string; fileName: string; kind: string; sizeBytes: number; uploadedAt?: string; uploadedBy?: { fullName?: string } }[] {
    const docs = this.form?.get('documents')?.value;
    if (docs?.length) {
      return docs;
    }
    return (this.form?.get('orderDocumentKeys')?.value || []).map((key: string) => ({
      s3Key: key,
      fileName: key.split('/').pop(),
      kind: 'Sonstiges',
      sizeBytes: 0,
    }));
  }

  public setDocumentKind(s3Key: string, kind: string): void {
    const docs = (this.form.get('documents').value || []).map(d => d.s3Key === s3Key ? { ...d, kind } : d);
    this.form.get('documents').patchValue(docs);
  }

  public applyOrder(value: IDeliveryOrder): void {
    if (value.orderer) {
      value.orderer.name = value.orderer.fullName;
    }
    value.orderProducts?.forEach(x => x.isBagable ? x.quantity = +(x.quantity / 1000).toFixed(2) : x.quantity);
    this._editObject = value;
    this.beforeLoadForm();
    this.form.patchValue(this.editObject);
    this.afterLoadForm();
    this.orderSynced.emit(this.editObject);
    this._cdr.detectChanges();
  }

  public hasPendingBooking(): boolean {
    return Object.values(this.rowBookState).some(s => s === 'booking' || s === 'error') || this.bookAllBusy;
  }

  public pendingBookingLabels(): string[] {
    return (this.productFormList?.controls || [])
      .filter(c => {
        const id = c.get('id')?.value;
        const state = this.rowBookState[id];
        return state === 'booking' || state === 'error';
      })
      .map(c => c.get('name')?.value || `#${c.get('id')?.value}`);
  }

  public requestClose(onClose: () => void): void {
    if (this.hasPendingBooking()) {
      this.pendingClose = onClose;
      this.closeConfirmVisible = true;
      this._cdr.detectChanges();
      return;
    }
    onClose();
  }

  public confirmForceClose(): void {
    this.closeConfirmVisible = false;
    this.pendingClose?.();
  }

  public reloadAfterConflict(): void {
    this.concurrencyVisible = false;
    this.fetchAndPatchOrder(() => {
      this.rowBookState = {};
      this.rowIdempotency = {};
    });
  }

  public bookedQty(): number {
    return (this.productFormList?.value || []).reduce((sum, row) => sum + (Number(row.deliveredQuantity) || 0), 0);
  }

  public orderedQty(): number {
    return (this.productFormList?.value || []).reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
  }

  public progressPercent(): number {
    const ordered = this.orderedQty();
    if (!ordered) {
      return 0;
    }
    return Math.round((this.bookedQty() / ordered) * 100);
  }

  public openPositionCount(): number {
    return (this.productFormList?.value || []).filter(row => this.rowOpenQty(row) > 0 && row.status === 'Open').length;
  }

  public rowOpenQty(row: { quantity?: number; deliveredQuantity?: number }): number {
    return Math.max(0, (Number(row.quantity) || 0) - (Number(row.deliveredQuantity) || 0));
  }

  public rowStatusKey(row: { id?: number; status?: string; quantity?: number; deliveredQuantity?: number }): string {
    const state = this.rowBookState[row.id];
    if (state === 'booking') {
      return 'booking';
    }
    if (state === 'error') {
      return 'error';
    }
    if (row.status === 'Canceled') {
      return 'canceled';
    }
    const delivered = Number(row.deliveredQuantity) || 0;
    const qty = Number(row.quantity) || 0;
    if (row.status === 'Finished' || (qty > 0 && delivered >= qty)) {
      return 'done';
    }
    if (delivered > 0 && delivered < qty) {
      return 'partial';
    }
    return 'open';
  }

  public rowStatusLabel(row: { id?: number; status?: string; quantity?: number; deliveredQuantity?: number }): string {
    switch (this.rowStatusKey(row)) {
      case 'booking': return 'wird gebucht';
      case 'error': return 'nicht gebucht';
      case 'done': return 'gebucht';
      case 'canceled': return 'storniert';
      case 'partial': return 'teilweise';
      default: return 'offen';
    }
  }

  public bookAllRows(): { name: string; ordered: number; delivered: number; willBook: number }[] {
    return (this.productFormList?.value || [])
      .filter(row => row.status === 'Open' && this.rowOpenQty(row) > 0)
      .map(row => ({
        name: row.name,
        ordered: Number(row.quantity) || 0,
        delivered: Number(row.deliveredQuantity) || 0,
        willBook: this.rowOpenQty(row),
      }));
  }

  public bookAllTotal(): number {
    return this.bookAllRows().reduce((sum, r) => sum + r.willBook, 0);
  }

  public openBookAll(): void {
    this.bookAllIdempotency = crypto.randomUUID();
    this.bookAllDeliveryDate = moment().toDate() as any;
    this.bookAllCharge = '';
    this.bookAllMhd = null;
    this.bookAllVisible = true;
  }

  public confirmBookAll(): void {
    if (!this.editObject?.['id'] || !this.bookAllRows().length) {
      return;
    }
    this.bookAllBusy = true;
    this._service.bookAllOutstanding(this.editObject['id'], {
      deliveryDate: this.bookAllDeliveryDate ? moment(this.bookAllDeliveryDate).format('YYYY-MM-DD') : null,
      mhd: this.bookAllMhd ? moment(this.bookAllMhd).format('YYYY-MM-DD') : null,
      charge: this.bookAllCharge,
      version: this.editObject['version'],
      idempotencyKey: this.bookAllIdempotency,
    }).subscribe({
      next: () => {
        this.bookAllBusy = false;
        this.bookAllVisible = false;
        this._notifyService.pushSuccess('Erfolgreich', 'Alles eingebucht');
        this.fetchAndPatchOrder(() => this._cdr.detectChanges());
      },
      error: (err) => {
        this.bookAllBusy = false;
        if (err?.InternalCode === 'OrderConcurrencyConflict') {
          this.concurrencyVisible = true;
        }
        this.parseError(err);
        this._cdr.detectChanges();
      },
    });
  }

  public openCorrect(rowIndex: number): void {
    const row = this.productFormList.at(rowIndex).getRawValue();
    this.correctRow = rowIndex;
    this.correctQuantity = Number(row.deliveredQuantity) || 0;
    this.correctReason = '';
    this.correctVisible = true;
  }

  public confirmCorrect(): void {
    const row = this.productFormList.at(this.correctRow).getRawValue();
    if (!this.correctReason?.trim()) {
      return;
    }
    this.correctBusy = true;
    this._service.correctProductOrder(row.id, {
      deliveredQuantity: this.correctQuantity,
      reason: this.correctReason,
      version: this.editObject['version'],
      idempotencyKey: crypto.randomUUID(),
    }).subscribe({
      next: () => {
        this.correctBusy = false;
        this.correctVisible = false;
        this._notifyService.pushSuccess('Erfolgreich', 'Menge korrigiert');
        this.fetchAndPatchOrder(() => this._cdr.detectChanges());
      },
      error: (err) => {
        this.correctBusy = false;
        if (err?.InternalCode === 'OrderConcurrencyConflict') {
          this.concurrencyVisible = true;
        }
        this.parseError(err);
        this._cdr.detectChanges();
      },
    });
  }

  public formatSize(bytes: number): string {
    if (!bytes) {
      return '–';
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private findProductGroupById(id: number): FormGroup | null {
    const idx = this.productFormList.controls.findIndex(c => c.get('id')?.value === id);
    return idx >= 0 ? this.productFormList.at(idx) as FormGroup : null;
  }

  private ensureUserInList(
    list: ISimpleListItem[] | undefined,
    user?: { id?: number | string; fullName?: string; name?: string; value?: string }
  ): ISimpleListItem[] {
    const current = list || [];
    if (user?.id == null) {
      return current;
    }

    const id = String(user.id);
    if (current.some(x => String(x.id) === id)) {
      return current;
    }

    return [{ id, value: user.value || user.fullName || user.name || '' }, ...current];
  }
}
